import L from "../vendor/leaflet/leaflet.js"
import {
  DIVE_MS,
  INTRO_DIVE_MS,
  INTRO_HOLD_MS,
  diveCenterAtZoom,
  diveEase,
  diveFocus,
  diveTargetZoom,
  flyDurationMs,
  flyPath,
  introTargetZoom,
} from "./mosaic_dive.js"
import {isWarming, progressText, shouldKeepPolling, warmingLabel} from "./mosaic_warming.js"
import {applyTileRevs, tileSrcNeedsUpdate, withTileRev} from "./mosaic_tile_rev.js"
import {formatSlotPostedAt} from "./mosaic_slot_meta.js"
import {
  TOUR_AUTO_CLOSE_MS,
  randomIntroSlot,
  readTourSeen,
  shouldRunTour,
  writeTourSeen,
} from "./mosaic_tour.js"
import {attachMosaicQa} from "./mosaic_qa.js"
import {prefetchAllowed, ringFor} from "./mosaic_prefetch.js"
import {stillOpacity, tileOpacity, wantsWrap as wrapWanted} from "./mosaic_bands.js"
import {paintCarryOver, take as takeSeam} from "./handoff_seam.js"

const PYRAMID_MIN_Z = 4
const TAP_MIN_PX = 44
const TILE_LOAD_PX = 4
const TILE_HIDE_PX = 3
const TILE_FADE_PX = 5
const STILL_OFF_PX = 28

export const MosaicMap = {
  mounted() {
    const slotCols = num(this.el.dataset.slotCols, 512)
    const slotRows = num(this.el.dataset.slotRows, 768)
    const tilePx = num(this.el.dataset.tilePx, 256)
    const slotNative = num(this.el.dataset.slotNativeZoom, 9)
    const slotMaxZoom = num(this.el.dataset.slotMaxZoom, 10)
    const mosaicUrl = this.el.dataset.mosaicUrl
    const tileTemplate = this.el.dataset.tileTemplate

    const slotW = slotCols * tilePx
    const slotH = slotRows * tilePx

    const map = L.map(this.el, {
      crs: L.CRS.Simple,
      zoomSnap: 0,
      zoomDelta: 0.15,
      wheelPxPerZoomLevel: 180,
      minZoom: 0,
      maxZoom: slotMaxZoom,
      zoomControl: false,
      attributionControl: false,
      inertia: true,
      bounceAtZoomLimits: true,
    })

    this.map = map

    // The poster is the **backdrop**, not a curtain. It sits in its own pane
    // *below* the tile pane, so a slot whose JPEG has not arrived shows the
    // campaign artwork instead of the map background. Before this it was above
    // the tiles (z 350) because Leaflet renders tiles with `plus-lighter` and a
    // poster underneath washed them out — measured on 2026-10-26: switching the
    // blend to normal and swapping the panes changes the settled wall by 3/255
    // mean, which is the price of never showing a black gap. Field evidence:
    // the 2026-10-26 recording shows black columns while panning at 48 px/slot.
    map.createPane("mosaicStill")
    map.getPane("mosaicStill").style.zIndex = "150"
    map.getPane("mosaicStill").style.pointerEvents = "none"

    const toLatLng = (x, y) => map.unproject([x, y], slotNative)
    const worldBounds = L.latLngBounds(toLatLng(0, 0), toLatLng(slotW, slotH))

    let still = null
    if (mosaicUrl) {
      still = L.imageOverlay(mosaicUrl, worldBounds, {
        interactive: false,
        pane: "mosaicStill",
        className: "mosaic-still",
      }).addTo(map)
    }

    // The AR handoff left its cover frame on screen. Paint the same still over
    // this map and fade it out once Leaflet has drawn: the viewer sees the
    // picture continue instead of a blank page. Consumed once, so a dive or a
    // reconnect never repaints a stale frame.
    const seam = takeSeam(window.sessionStorage)
    let seamDone = null
    if (seam) {
      const carry = paintCarryOver({
        container: this.el,
        url: seam.url,
        map,
      })
      if (carry) {
        this._seamEl = carry.img
        seamDone = carry.done
      }
    }
    // The intro dive must not start under the carried frame: the picture would
    // jump while it is still fading. Hold the first camera move until the
    // seam has cleared.
    const seamCleared = seamDone || Promise.resolve()

    const zoomForPx = (px) => slotNative + Math.log2(px / tilePx)
    const tileMinZoom = zoomForPx(TILE_LOAD_PX)

    const tileRev = {}
    this._tileRev = tileRev

    // The lookahead ring. Leaflet's `_update` asks for exactly the tiles
    // inside `_getTiledPixelBounds`, and `keepBuffer` only prunes tiles that
    // already exist: it never creates one. (Measured 2026-10-21: raising
    // `keepBuffer` to 3 changed the pan gap from 13/44 frames to 10/44.) So
    // widening the layer's own idea of the viewport is the lever, and the tile
    // set comes along because a tile inside those bounds is `current`, which
    // is what keeps it through pruning. Policy, caps and reasons:
    // mosaic_prefetch.js. Measured 2026-11-02 at 51 CSS px per cell: the share
    // of a half-screen pan that was already decoded went 0.53 -> 1.00 right and
    // 0.72 -> 1.00 down; a full screen right went 0.02 -> 1.00.
    const lookahead = {active: false, ring: 0}
    this._lookahead = lookahead

    const WrapTiles = L.TileLayer.extend({
      _getTiledPixelBounds(center) {
        const bounds = L.TileLayer.prototype._getTiledPixelBounds.call(this, center)
        if (!lookahead.active) return bounds

        // `bounds` is the tight viewport measured in tile pixels at
        // `_tileZoom`, so its own size is the viewport span in tiles: the same
        // numbers `ringTileEstimate` is judged on, with nothing re-derived.
        const tileSize = this.getTileSize()
        const span = bounds.getSize()
        const pad = ringFor({
          cols: span.x / tileSize.x,
          rows: span.y / tileSize.y,
        })
        lookahead.ring = pad
        if (!pad) return bounds

        // Pad per axis, in pixels. `Bounds.pad` scales x and y by the same
        // fraction of their own size, which would give a phone a different
        // ring on each axis; the tiles are square, so the ring is too.
        const dx = pad * tileSize.x
        const dy = pad * tileSize.y
        return new L.Bounds(bounds.min.subtract([dx, dy]), bounds.max.add([dx, dy]))
      },
      _isValidTile(coords) {
        if (coords.z < PYRAMID_MIN_Z || coords.z > slotNative) return false
        const span = 2 ** (slotNative - coords.z)
        const tilesX = slotCols / span
        const tilesY = slotRows / span
        return (
          coords.x >= -tilesX &&
          coords.x < tilesX * 2 &&
          coords.y >= -tilesY &&
          coords.y < tilesY * 2
        )
      },
      getTileUrl(coords) {
        const z = clamp(coords.z, PYRAMID_MIN_Z, slotNative)
        const span = 2 ** (slotNative - z)
        const tilesX = slotCols / span
        const tilesY = slotRows / span
        const x = posMod(coords.x, tilesX)
        const y = posMod(coords.y, tilesY)
        const url = L.Util.template(tileTemplate, {x, y, z})
        return withTileRev(url, z, x, y, tileRev)
      },
    })

    const tiles = tileTemplate
      ? new WrapTiles(tileTemplate, {
          minZoom: tileMinZoom,
          maxZoom: slotMaxZoom,
          minNativeZoom: PYRAMID_MIN_Z,
          maxNativeZoom: slotNative,
          noWrap: false,
          // Prune margin only, and deliberately 2. Measured 2026-10-21: a
          // fresh zoom already issues ~32 tiles, each an on-demand Vix join of
          // ~1.5 s on the dev server; a wider prune margin deepens the queue
          // behind slow tiles and starves the slot card's own image. It cannot
          // preload anything either: `keepBuffer` decides which tiles that
          // already exist survive pruning, never which are requested. The
          // lookahead ring above is what fetches the ring.
          keepBuffer: 2,
          updateWhenIdle: false,
          updateWhenZooming: true,
          opacity: 0,
          errorTileUrl: "/images/mosaic-cell-placeholder.jpg",
          pane: "tilePane",
          className: "mosaic-tiles",
        })
      : null

    map.invalidateSize()

    let filledOnce = false
    let diving = false
    const fillViewport = () => {
      map.invalidateSize()
      if (diving) return
      const size = map.getSize()
      if (size.x < 8 || size.y < 8) {
        window.requestAnimationFrame(fillViewport)
        return
      }

      const contain = map.getBoundsZoom(worldBounds, false)
      const cover = map.getBoundsZoom(worldBounds, true)
      let zoom = cover
      if (!Number.isFinite(zoom) || zoom > slotMaxZoom || zoom < contain - 12) {
        zoom = contain
      }
      if (!Number.isFinite(zoom)) return

      map.setMinZoom(zoom)
      if (!filledOnce || map.getZoom() < zoom - 0.001) {
        map.setView(worldBounds.getCenter(), zoom, {animate: false})
        filledOnce = true
      }
      if (!wrapped) map.setMaxBounds(worldBounds)
    }

    const syncRotateHint = () => {
      const el = document.getElementById("mosaic-rotate")
      if (!el) return
      const landscape = window.matchMedia("(orientation: landscape) and (max-width: 1024px)").matches
      el.toggleAttribute("hidden", !landscape)
    }

    const pxPerCell = () => tilePx * 2 ** (map.getZoom() - slotNative)

    let wrapped = false
    let tilesAttached = false
    let seenTileLoad = false

    const syncBands = () => {
      const px = pxPerCell()
      const tileMix = smoothstep(TILE_FADE_PX, STILL_OFF_PX, px)
      const stillEl = still?.getElement()

      if (tiles) {
        if (px >= TILE_LOAD_PX && !tilesAttached) {
          tiles.addTo(map)
          tilesAttached = true
        } else if (px < TILE_HIDE_PX && tilesAttached) {
          tiles.setOpacity(0)
          map.removeLayer(tiles)
          tilesAttached = false
        }

        tiles.setOpacity(tileOpacity({tileMix, attached: tilesAttached, seenTileLoad}))
      }

      if (stillEl) {
        // The poster is the backdrop: always painted, always opaque. A slot
        // whose JPEG is still in flight shows the artwork, not the map.
        stillEl.style.opacity = String(stillOpacity({hasPoster: Boolean(mosaicUrl)}))
        stillEl.style.filter = "none"
        stillEl.classList.remove("mosaic-still-hidden")
      }

      const wantWrap = wrapWanted({seenTileLoad, tileMix})
      if (wantWrap !== wrapped) {
        wrapped = wantWrap
        map.setMaxBounds(wantWrap ? null : worldBounds)
      }
    }

    // Lookahead scheduling. The ring exists for the *next* pan, so it is only
    // built while the camera is parked: `movestart`/`zoomstart` disarm it, and
    // it re-arms after the move settles, once every tile the layer holds has
    // arrived (arming into a loading layer would queue the ring ahead of the
    // screen). Leaflet's own `load` is the second half of that handshake.
    const device = navigator.connection || navigator.mozConnection || navigator.webkitConnection
    const allowed = prefetchAllowed({
      saveData: Boolean(device?.saveData),
      effectiveType: device?.effectiveType ?? null,
    })

    let ringWanted = false
    let ringZoom = null

    const setLookahead = (active) => {
      if (!tiles || lookahead.active === active) return
      lookahead.active = active
      lookahead.ring = 0
      // Visible in the DOM and in the QA sample: "was the ring on?" is the one
      // fact that explains whether a pan had to fetch its own screen.
      this.el.dataset.prefetch = active ? "1" : "0"
      // Leaflet's own "recompute my tile set". Held tiles are already
      // `current`, so arming only adds the ring.
      if (active) tiles._update?.()
    }
    const armLookahead = () => {
      if (!ringWanted || !allowed || !tilesAttached || diving || !tiles) return
      if (pxPerCell() < STILL_OFF_PX) return
      // A new zoom level is a whole new tile set: let the screen fill first.
      if (ringZoom !== map.getZoom()) return
      if (tiles.isLoading()) return
      setLookahead(true)
    }

    const settleLookahead = () => {
      ringWanted = true
      ringZoom = map.getZoom()
      armLookahead()
    }

    if (tiles) {
      tiles.on("load", () => {
        seenTileLoad = true
        syncBands()
        armLookahead()
      })
    }

    map.on("zoom", syncBands)
    map.on("zoomend", syncBands)
    map.on("zoomend", settleLookahead)
    map.on("moveend", settleLookahead)
    map.on("movestart", () => {
      ringWanted = false
      setLookahead(false)
    })
    map.on("zoomstart", () => {
      ringWanted = false
      ringZoom = null
      setLookahead(false)
    })
    map.on("resize", () => {
      fillViewport()
      syncRotateHint()
      syncBands()
    })
    this._onOrient = () => {
      window.setTimeout(() => {
        fillViewport()
        syncRotateHint()
        syncBands()
      }, 250)
    }
    window.addEventListener("orientationchange", this._onOrient)
    still?.on("load", () => {
      fillViewport()
      syncBands()
    })

    fillViewport()
    syncRotateHint()
    syncBands()

    this.bindWarming()
    this.bindSlotCard({map, slotCols, slotRows, tilePx, slotNative, tileTemplate})

    const cancelDive = () => {
      if (this._diveRaf) {
        window.cancelAnimationFrame(this._diveRaf)
        this._diveRaf = 0
      }
      if (this._introHoldTimer) {
        window.clearTimeout(this._introHoldTimer)
        this._introHoldTimer = 0
      }
      if (this._tourAutoCloseTimer) {
        window.clearTimeout(this._tourAutoCloseTimer)
        this._tourAutoCloseTimer = 0
      }
    }

    const freezeMap = () => {
      map.dragging.disable()
      map.touchZoom.disable()
      map.scrollWheelZoom.disable()
      map.doubleClickZoom.disable()
    }

    const thawMap = () => {
      map.dragging.enable()
      map.touchZoom.enable()
      map.scrollWheelZoom.enable()
      map.doubleClickZoom.enable()
    }

    const coverZoom = () => {
      const contain = map.getBoundsZoom(worldBounds, false)
      const cover = map.getBoundsZoom(worldBounds, true)
      let zoom = cover
      if (!Number.isFinite(zoom) || zoom > slotMaxZoom || zoom < contain - 12) {
        zoom = contain
      }
      return zoom
    }

    const animateFocusDive = ({latlng, targetZoom, durationMs, onDone}) => {
      cancelDive()
      map.stop()
      diving = true
      map.setMaxBounds(null)
      freezeMap()
      const startZoom = map.getZoom()
      const startFocus = map.latLngToContainerPoint(latlng)
      const t0 = performance.now()

      const frame = (now) => {
        this._diveRaf = 0
        const t = Math.min(1, (now - t0) / durationMs)
        const u = diveEase(t)
        const z = startZoom + (targetZoom - startZoom) * u
        const half = map.getSize().divideBy(2)
        const focus = diveFocus(startFocus, half, u)
        const slotAtZ = map.project(latlng, z)
        const centerPt = diveCenterAtZoom(slotAtZ, focus, half)
        const center = map.unproject([centerPt.x, centerPt.y], z)
        map.setView(center, z, {animate: false})
        if (t < 1) {
          this._diveRaf = window.requestAnimationFrame(frame)
          return
        }
        onDone()
      }

      this._diveRaf = window.requestAnimationFrame(frame)
    }

    const animateCover = (onDone) => {
      cancelDive()
      map.stop()
      diving = true
      const startZoom = map.getZoom()
      const startCenter = map.getCenter()
      const targetZoom = coverZoom()
      const targetCenter = worldBounds.getCenter()
      const t0 = performance.now()

      const frame = (now) => {
        this._diveRaf = 0
        const t = Math.min(1, (now - t0) / INTRO_DIVE_MS)
        const u = diveEase(t)
        const z = startZoom + (targetZoom - startZoom) * u
        const lat =
          startCenter.lat + (targetCenter.lat - startCenter.lat) * u
        const lng =
          startCenter.lng + (targetCenter.lng - startCenter.lng) * u
        map.setView([lat, lng], z, {animate: false})
        if (t < 1) {
          this._diveRaf = window.requestAnimationFrame(frame)
          return
        }
        onDone()
      }

      this._diveRaf = window.requestAnimationFrame(frame)
    }

    const animateCoverHop = ({latlng, targetZoom, onDone}) => {
      cancelDive()
      map.stop()
      diving = true
      map.setMaxBounds(null)
      freezeMap()
      const startZoom = map.getZoom()
      const startCenter = map.getCenter()
      const from = map.project(startCenter, startZoom)
      const to = map.project(latlng, startZoom)
      const size = map.getSize()
      const path = flyPath({
        fromX: from.x,
        fromY: from.y,
        toX: to.x,
        toY: to.y,
        startZoom,
        targetZoom,
        sizeX: size.x,
        sizeY: size.y,
      })
      const durationMs = flyDurationMs(path.S)
      const t0 = performance.now()

      const frame = (now) => {
        this._diveRaf = 0
        const t = Math.min(1, (now - t0) / durationMs)
        const step = path.at(t)
        const center = map.unproject([step.x, step.y], startZoom)
        map.setView(center, step.zoom, {animate: false})
        syncBands()
        if (t < 1) {
          this._diveRaf = window.requestAnimationFrame(frame)
          return
        }
        onDone()
      }

      this._diveRaf = window.requestAnimationFrame(frame)
    }

    const flyToSlot = (x, y, {cover = false} = {}) => {
      const run = () => {
        if (!filledOnce) {
          window.requestAnimationFrame(run)
          return
        }
        this._closeSlotCardSilent?.()
        const latlng = toLatLng((x + 0.5) * tilePx, (y + 0.5) * tilePx)
        const targetZoom = diveTargetZoom(slotMaxZoom, slotNative)
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches

        const finish = () => {
          cancelDive()
          diving = false
          map.setView(latlng, targetZoom, {animate: false})
          syncBands()
          this._openSlotCard?.(x, y)
        }

        if (reduceMotion) {
          cancelDive()
          map.stop()
          diving = true
          map.setMaxBounds(null)
          finish()
          return
        }

        if (cover) {
          animateCoverHop({
            latlng,
            targetZoom,
            onDone: finish,
          })
          return
        }

        animateFocusDive({
          latlng,
          targetZoom,
          durationMs: DIVE_MS,
          onDone: finish,
        })
      }
      run()
    }

    this._cancelDive = () => {
      cancelDive()
      diving = false
    }

    this.handleEvent("dive_to_slot", ({x, y, cover}) => {
      this._slotDive = true
      this.abortTour?.({resume: false})
      flyToSlot(Number(x), Number(y), {cover: Boolean(cover)})
    })

    this.handleEvent("occupancy_tiles", ({tiles: incoming}) => {
      applyTileRevs(tileRev, incoming || [])
      if (!tiles?._tiles) return
      for (const key of Object.keys(tiles._tiles)) {
        const tile = tiles._tiles[key]
        if (!tile?.el || !tile.coords) continue
        const url = tiles.getTileUrl(tile.coords)
        if (tileSrcNeedsUpdate(tile.el, url)) tile.el.src = url
      }
    })

    this.bindTour({
      map,
      slotCols,
      slotRows,
      tilePx,
      slotNative,
      animateFocusDive,
      animateCover,
      coverZoom,
      toLatLng,
      freezeMap,
      thawMap,
      cancelDive,
      syncBands,
      worldBounds,
      getFilledOnce: () => filledOnce,
      seamCleared,
      fromSeam: Boolean(seam),
      setDiving: (value) => {
        diving = value
      },
      // A getter, not a mirrored boolean: every dive mutates the local
      // `diving` directly (animateFocusDive, animateCover, animateCoverHop,
      // flyToSlot), so a write-through setter left the QA sample permanently
      // `diving: false` — the one flag that records the interaction the
      // recorder exists to measure.
      getDiving: () => diving,
    })

    // The sampler reads `hook.getDiving`, not the tour options. `bindTour`
    // never stored that getter, so every mosaic sample was `diving: false`.
    this.getDiving = () => diving
    this._qa = attachMosaicQa(this)
  },

  bindHint() {
    const hint = document.getElementById("mosaic-hint")
    if (!hint || !this.map) return

    hint.removeAttribute("hidden")
    const hide = () => hint.setAttribute("hidden", "hidden")
    this.map.once("zoomstart", hide)
    this.map.once("dragstart", hide)
    this._hintTimer = window.setTimeout(hide, 3200)
  },

  hideHint() {
    const hint = document.getElementById("mosaic-hint")
    if (hint) hint.setAttribute("hidden", "hidden")
    if (this._hintTimer) {
      window.clearTimeout(this._hintTimer)
      this._hintTimer = 0
    }
  },

  bindTour({
    map,
    slotCols,
    slotRows,
    tilePx,
    slotNative,
    animateFocusDive,
    animateCover,
    coverZoom,
    toLatLng,
    freezeMap,
    thawMap,
    cancelDive,
    syncBands,
    worldBounds,
    getFilledOnce,
    seamCleared,
    fromSeam,
    setDiving,
  }) {
    const overlay = document.getElementById("mosaic-tour")
    const closeBtn = document.getElementById("mosaic-tour-close")
    const hasSlotDive = Boolean(this.el.dataset.diveX) && this.el.dataset.diveX !== ""
    const reduceMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches
    let started = false
    let waitingWarm = false

    const hideOverlay = () => {
      if (!overlay) return
      overlay.setAttribute("hidden", "hidden")
    }

    const showOverlay = () => {
      if (!overlay) return
      overlay.removeAttribute("hidden")
      closeBtn?.focus()
    }

    const finishCover = () => {
      cancelDive()
      setDiving(false)
      const zoom = coverZoom()
      if (Number.isFinite(zoom)) {
        map.setView(worldBounds.getCenter(), zoom, {animate: false})
      }
      syncBands()
      thawMap()
    }

    this.abortTour = ({resume} = {resume: true}) => {
      waitingWarm = false
      hideOverlay()
      cancelDive()
      if (resume) {
        setDiving(false)
        thawMap()
        syncBands()
      }
    }

    // The tour closes on its own so a visitor who has just come through the AR
    // handoff is not held at an explanation. Unlike `dismiss` it leaves the
    // camera where the intro landed: the photos must stay on screen, which is
    // the whole point of the dive.
    const autoClose = () => {
      this._tourAutoCloseTimer = 0
      if (!overlay || overlay.hasAttribute("hidden")) return
      writeTourSeen()
      hideOverlay()
      // `showOverlay` focused the close button; hiding its dialog would leave
      // focus on a hidden node. Hand it to the map so the keyboard survives.
      const mapEl = document.getElementById("mosaic-root")
      if (mapEl && typeof mapEl.focus === "function") {
        mapEl.setAttribute("tabindex", "-1")
        mapEl.focus({preventScroll: true})
      }
      setDiving(false)
      thawMap()
      syncBands()
      // The card explains three gestures; the hint that replaces it names the
      // two that matter for exploring, so closing early does not hide them.
      this.bindHint()
    }

    const dismiss = () => {
      writeTourSeen()
      hideOverlay()
      if (this._tourAutoCloseTimer) {
        window.clearTimeout(this._tourAutoCloseTimer)
        this._tourAutoCloseTimer = 0
      }
      if (reduceMotion()) {
        finishCover()
        return
      }
      freezeMap()
      animateCover(finishCover)
    }

    closeBtn?.addEventListener("click", dismiss)
    this._onTourEsc = (event) => {
      if (event.key !== "Escape" || !overlay || overlay.hasAttribute("hidden")) return
      event.preventDefault()
      dismiss()
    }
    window.addEventListener("keydown", this._onTourEsc)

    const begin = () => {
      if (started || this._slotDive) return
      started = true
      this.hideHint()
      // Wait for the AR carry-over to fade before the camera moves: the
      // picture must not jump while it is still on screen.
      seamCleared.then(beginDive)
    }

    const beginDive = () => {
      if (this._slotDive) return

      const waitFill = () => {
        if (!getFilledOnce()) {
          window.requestAnimationFrame(waitFill)
          return
        }
        if (this._slotDive) return
        const {x, y} = randomIntroSlot(slotCols, slotRows)
        const latlng = toLatLng((x + 0.5) * tilePx, (y + 0.5) * tilePx)
        const targetZoom = introTargetZoom(slotNative, tilePx)
        const holdThenOverlay = () => {
          // The dive is over. The poster stays painted underneath as the
          // backdrop for slots that have not arrived, so nothing here decides
          // whether the photos are visible.
          setDiving(false)
          map.setView(latlng, targetZoom, {animate: false})
          syncBands()
          freezeMap()
          this._introHoldTimer = window.setTimeout(() => {
            this._introHoldTimer = 0
            showOverlay()
            this._tourAutoCloseTimer = window.setTimeout(autoClose, TOUR_AUTO_CLOSE_MS)
          }, INTRO_HOLD_MS)
        }

        if (reduceMotion()) {
          holdThenOverlay()
          return
        }

        animateFocusDive({
          latlng,
          targetZoom,
          durationMs: INTRO_DIVE_MS,
          onDone: holdThenOverlay,
        })
      }

      waitFill()
    }

    const tryStart = () => {
      const warmingEl = document.getElementById("mosaic-warming")
      const warming = Boolean(warmingEl && !warmingEl.hasAttribute("hidden"))
      if (hasSlotDive || this._slotDive) return
      if (warming) {
        waitingWarm = true
        return
      }
      if (
        !overlay ||
        !shouldRunTour({
          seen: readTourSeen(),
          hasSlotDive: false,
          warming: false,
          fromSeam,
        })
      ) {
        this.bindHint()
        return
      }
      begin()
    }

    this._onWarmingChange = (warming) => {
      if (waitingWarm && !warming) {
        waitingWarm = false
        tryStart()
      }
    }

    tryStart()
  },

  bindWarming() {
    const url = this.el.dataset.mosaicStatusUrl
    const overlay = document.getElementById("mosaic-warming")
    const progress = document.getElementById("mosaic-warming-progress")
    const label = document.getElementById("mosaic-warming-label")
    if (!url || !overlay) return

    const apply = (data) => {
      overlay.toggleAttribute("hidden", !isWarming(data))
      if (progress) progress.textContent = progressText(data)
      if (label) label.textContent = warmingLabel(data)
      this._onWarmingChange?.(!overlay.hasAttribute("hidden"))
      return shouldKeepPolling(data)
    }

    const warmingAttr = this.el.getAttribute("data-mosaic-warming")
    if (warmingAttr !== null && warmingAttr !== "false") {
      apply({status: "warming", done: 0, total: 0})
    }

    const poll = async () => {
      try {
        const res = await fetch(url, {headers: {accept: "application/json"}})
        if (!res.ok) return
        const data = await res.json()
        if (!apply(data)) this.stopWarmingPoll()
      } catch (_error) {
        // Transient network errors keep polling until ready or the view leaves.
      }
    }

    poll()
    this._warmingTimer = window.setInterval(poll, 1500)
  },

  stopWarmingPoll() {
    if (this._warmingTimer) {
      window.clearInterval(this._warmingTimer)
      this._warmingTimer = 0
    }
  },

  bindSlotCard({map, slotCols, slotRows, tilePx, slotNative, tileTemplate}) {
    const mapEl = document.getElementById("mosaic-root")
    const root = document.getElementById("slot-card-root")
    if (!mapEl || !root) return
    const slug = this.el.dataset.slug

    const photo = document.getElementById("slot-card-photo")
    const nameEl = document.getElementById("slot-card-name")
    const nameLink = document.getElementById("slot-card-name-link")
    const metaEl = document.getElementById("slot-card-meta")
    const captionEl = document.getElementById("slot-card-caption")
    const translateBtn = document.getElementById("slot-card-translate")
    const translateLabel = document.getElementById("slot-card-translate-label")
    const translateSpinner = translateBtn?.querySelector(".slot-card-translate-spinner")
    const translateError = document.getElementById("slot-card-translate-error")
    const likesEl = document.getElementById("slot-card-likes")
    const likeBtn = document.getElementById("slot-card-like")
    const avatarEl = document.getElementById("slot-card-avatar")
    const shareBtn = document.getElementById("slot-card-share")
    const shareToast = document.getElementById("slot-share-toast")
    const nextBtn = document.getElementById("slot-card-next")
    const reportBtn = document.getElementById("slot-card-report")
    const reportLabel = document.getElementById("slot-card-report-label")
    const moreMenu = document.getElementById("slot-card-more")

    const sessionState = new Map()
    let currentKey = null
    let currentSlot = null
    let shareUrl = window.location.href
    let toastTimer = 0
    let originalCaption = ""
    let translatedCaption = ""
    let showingTranslation = false
    let translateTimer = 0
    let pendingUpgrade = null

    const clearUpgrade = () => {
      if (!pendingUpgrade) return
      pendingUpgrade.onload = null
      pendingUpgrade.onerror = null
      pendingUpgrade = null
    }

    const slotKey = (x, y) => `${x},${y}`

    const loadState = (x, y, mock) => {
      const key = slotKey(x, y)
      if (!sessionState.has(key)) {
        sessionState.set(key, {
          liked: false,
          likes: mock.likes,
        })
      }
      return sessionState.get(key)
    }

    const renderLike = (state) => {
      likeBtn.classList.toggle("is-liked", state.liked)
      likeBtn.setAttribute("aria-pressed", state.liked ? "true" : "false")
      likesEl.textContent = String(state.likes)
    }

    const closeMoreMenu = () => {
      if (moreMenu) moreMenu.open = false
    }

    const setReportChrome = (reply = {}) => {
      const closed = Boolean(reply.report_closed)
      const reported = Boolean(reply.reported)
      const can = reply.can_report === true && !closed
      const showMenu = can || reported
      closeMoreMenu()
      if (moreMenu) moreMenu.hidden = !showMenu
      if (!reportBtn) return
      reportBtn.hidden = !showMenu
      reportBtn.disabled = !can
      if (reportLabel) reportLabel.textContent = reported ? "Reported" : "Report"
    }

    this.handleEvent("report_chrome", (payload) => setReportChrome(payload))

    const cellPx = () => tilePx * 2 ** (map.getZoom() - slotNative)
    const tappable = () => cellPx() >= TAP_MIN_PX
    const reduceMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches

    const setMapInteractive = (on) => {
      if (on) {
        map.dragging.enable()
        map.touchZoom.enable()
        map.scrollWheelZoom.enable()
        map.doubleClickZoom.enable()
      } else {
        map.dragging.disable()
        map.touchZoom.disable()
        map.scrollWheelZoom.disable()
        map.doubleClickZoom.disable()
      }
    }

    const setChromeHidden = (hidden) => {
      document.body.classList.toggle("mosaic-card-open", hidden)
    }

    const silentClose = () => {
      mapEl.classList.remove("slot-card-open")
      setChromeHidden(false)
      root.classList.remove("is-open")
      root.hidden = true
      closeMoreMenu()
      if (nextBtn) nextBtn.hidden = true
      resetTranslateChrome()
      clearUpgrade()
      currentSlot = null
      showingTranslation = false
      setMapInteractive(true)
    }

    const close = () => {
      mapEl.classList.remove("slot-card-open")
      setChromeHidden(false)
      root.classList.remove("is-open")
      closeMoreMenu()
      clearUpgrade()
      currentSlot = null
      setMapInteractive(true)
      this.pushEvent("clear_slot", {})
      const hide = () => {
        root.hidden = true
        root.removeEventListener("transitionend", hide)
      }
      if (reduceMotion()) {
        hide()
        return
      }
      root.addEventListener("transitionend", hide)
      window.setTimeout(hide, 320)
    }

    const reveal = () => {
      root.hidden = false
      mapEl.classList.add("slot-card-open")
      setChromeHidden(true)
      setMapInteractive(false)
      if (reduceMotion()) {
        root.classList.add("is-open")
        return
      }
      root.classList.remove("is-open")
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => root.classList.add("is-open"))
      })
    }

    const open = (x, y) => {
      this.pushEvent("show_slot", {x, y}, (reply) => {
        if (!reply?.occupied) return

        currentKey = slotKey(x, y)
        currentSlot = {x, y}
        const openedFor = currentSlot
        const likes = Number.isFinite(reply.likes) ? reply.likes : 0
        const state = loadState(x, y, {likes})
        const z = slotNative
        // Show the cell the map already downloaded, then upgrade to the master
        // when it decodes. A cold master is a 0.5–1.2 MB read that can queue
        // behind a bake on a slow link; sourcing it first opened the card with
        // a blank well and a half-height card, then popped. The cell gives the
        // real aspect immediately, so the upgrade never reflows the card.
        const cellUrl = L.Util.template(tileTemplate, {x, y, z})
        const masterUrl = reply.photo_uid
          ? `/e/${encodeURIComponent(slug)}/mosaic/p/${encodeURIComponent(reply.photo_uid)}/master`
          : null
        clearUpgrade()
        // The card image must beat the tile queue. A fresh zoom asks for ~30
        // on-demand Vix joins at once, each taking ~1.5 s on the dev server,
        // and an unprioritised card image waits behind all of them on a slow
        // link — the card then opens blank and pops a second later.
        photo.fetchPriority = "high"
        photo.decoding = "sync"
        // Reserve the well until a frame paints, then let the natural aspect
        // size the card (a panorama must not be cropped).
        photo.classList.add("is-loading")
        photo.onload = () => photo.classList.remove("is-loading")
        photo.onerror = () => photo.classList.remove("is-loading")
        // Cell first: it is in the tile cache, so the well is never blank
        // while a cold master read is in flight (or stalled).
        photo.src = cellUrl
        if (masterUrl) {
          const upgrade = new Image()
          upgrade.decoding = "async"
          upgrade.onload = () => {
            // The card may have been closed or moved on while the master
            // decoded; then this frame is stale and must not paint.
            if (currentSlot !== openedFor) return
            photo.src = masterUrl
          }
          upgrade.onerror = () => {}
          upgrade.src = masterUrl
          pendingUpgrade = upgrade
        }
        // A public handle links to the owner's profile. The name stays plain
        // text for seed/warehouse cards, and for handles the owner hid.
        if (nameLink) {
          const handle = reply.username
          const linkable = Boolean(handle) && (reply.kind === "live" || reply.kind === "expired")
          nameLink.hidden = !linkable
          nameEl.hidden = linkable
          nameLink.textContent = reply.name || ""
          if (linkable) {
            nameLink.href = `/u/${encodeURIComponent(handle)}`
            nameLink.setAttribute("aria-label", `${reply.name || handle} — open profile`)
          } else {
            nameLink.removeAttribute("href")
          }
        }
        if (!nameLink || nameLink.hidden) nameEl.textContent = reply.name || ""
        metaEl.textContent = formatSlotPostedAt(reply.posted_at)
        originalCaption = reply.caption || ""
        translatedCaption = reply.translated_caption || ""
        showingTranslation = Boolean(translatedCaption)
        captionEl.textContent = showingTranslation ? translatedCaption : originalCaption
        photo.alt = originalCaption || reply.name || ""
        if (translateBtn) {
          const can = Boolean(reply.can_translate) && Boolean(originalCaption)
          translateBtn.hidden = !can
          setTranslateBusy(false)
          setTranslateError(false)
          setTranslateLabel(showingTranslation ? "See original" : "Translate")
        }
        avatarEl.style.backgroundImage = ""
        avatarEl.style.background = ""
        if (reply.avatar_url) {
          avatarEl.style.backgroundImage = `url("${reply.avatar_url}")`
          avatarEl.style.backgroundSize = "cover"
        } else if (reply.avatar) {
          avatarEl.style.background = reply.avatar
        }
        renderLike(state)
        shareUrl = reply.path
          ? new URL(reply.path, window.location.origin).href
          : window.location.href
        if (nextBtn) nextBtn.hidden = !reply.next
        setReportChrome(reply)
        reveal()
      })
    }

    const syncCursor = () => {
      map.getContainer().style.cursor = tappable() ? "pointer" : ""
    }
    map.on("zoom", syncCursor)
    map.on("zoomend", syncCursor)
    syncCursor()

    const el = map.getContainer()
    const pointers = new Map()
    let pinching = false

    const slotFromClient = (clientX, clientY) => {
      const rect = el.getBoundingClientRect()
      const latlng = map.containerPointToLatLng([clientX - rect.left, clientY - rect.top])
      const point = map.project(latlng, slotNative)
      return {
        x: posMod(Math.floor(point.x / tilePx), slotCols),
        y: posMod(Math.floor(point.y / tilePx), slotRows),
      }
    }

    const onPointerDown = (event) => {
      if (event.button && event.button !== 0) return
      pointers.set(event.pointerId, {
        x: event.clientX,
        y: event.clientY,
        zoom: map.getZoom(),
        t: performance.now(),
      })
      if (pointers.size > 1) pinching = true
      this._pointerCount = pointers.size
      this._pinching = pinching
    }

    const onPointerUp = (event) => {
      const start = pointers.get(event.pointerId)
      pointers.delete(event.pointerId)
      this._pointerCount = pointers.size
      if (pointers.size === 0) {
        const wasPinch = pinching
        pinching = false
        this._pinching = false
        if (wasPinch) return
      }
      if (!start || pinching || !tappable() || !root.hidden) return
      if (performance.now() - start.t > 650) return
      if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > 16) return
      if (Math.abs(map.getZoom() - start.zoom) > 0.04) return
      const slot = slotFromClient(event.clientX, event.clientY)
      open(slot.x, slot.y)
    }

    const onPointerCancel = (event) => {
      pointers.delete(event.pointerId)
      this._pointerCount = pointers.size
      if (pointers.size === 0) this._pinching = false
      if (pointers.size < 2) pinching = pointers.size > 1
    }

    el.addEventListener("pointerdown", onPointerDown)
    window.addEventListener("pointerup", onPointerUp)
    window.addEventListener("pointercancel", onPointerCancel)
    this._tapCleanup = () => {
      el.removeEventListener("pointerdown", onPointerDown)
      window.removeEventListener("pointerup", onPointerUp)
      window.removeEventListener("pointercancel", onPointerCancel)
    }

    likeBtn?.addEventListener("click", () => {
      if (!currentKey) return
      const state = sessionState.get(currentKey)
      if (!state) return
      state.liked = !state.liked
      state.likes += state.liked ? 1 : -1
      renderLike(state)
    })

    const showCopied = () => {
      if (!shareToast) return
      shareToast.hidden = false
      window.clearTimeout(toastTimer)
      toastTimer = window.setTimeout(() => {
        shareToast.hidden = true
      }, 1800)
    }

    shareBtn?.addEventListener("click", async () => {
      const url = shareUrl || window.location.href
      const title = nameEl?.textContent || "Mosaic"
      const text = captionEl?.textContent || ""
      if (navigator.share) {
        try {
          await navigator.share({title, text, url})
          return
        } catch (error) {
          if (error?.name === "AbortError") return
        }
      }
      try {
        await navigator.clipboard.writeText(url)
        showCopied()
      } catch (_error) {
        window.prompt("Copy link", url)
      }
    })

    nextBtn?.addEventListener("click", (event) => {
      event.stopPropagation()
      this.pushEvent("next_photo", {})
    })

    const setTranslateLabel = (text) => {
      if (translateLabel) translateLabel.textContent = text
    }

    const setTranslateBusy = (busy) => {
      if (!translateBtn) return
      translateBtn.disabled = busy
      translateBtn.setAttribute("aria-busy", busy ? "true" : "false")
      if (translateSpinner) translateSpinner.hidden = !busy
    }

    const setTranslateError = (on) => {
      if (translateError) translateError.hidden = !on
    }

    const resetTranslateChrome = () => {
      window.clearTimeout(translateTimer)
      if (!translateBtn) return
      translateBtn.hidden = true
      setTranslateBusy(false)
      setTranslateError(false)
      setTranslateLabel("Translate")
    }

    const failTranslate = (silent) => {
      window.clearTimeout(translateTimer)
      setTranslateBusy(false)
      setTranslateLabel("Translate")
      setTranslateError(!silent)
    }

    const applyTranslation = (body) => {
      if (!body || body.includes("source_lang_code:")) {
        failTranslate()
        return
      }
      window.clearTimeout(translateTimer)
      translatedCaption = body
      showingTranslation = true
      captionEl.textContent = translatedCaption
      setTranslateBusy(false)
      setTranslateError(false)
      setTranslateLabel("See original")
    }

    translateBtn?.addEventListener("click", (event) => {
      event.stopPropagation()
      if (!currentSlot || translateBtn.hidden || translateBtn.disabled) return
      setTranslateError(false)
      if (showingTranslation) {
        showingTranslation = false
        captionEl.textContent = originalCaption
        setTranslateLabel("Translate")
        return
      }
      if (translatedCaption) {
        showingTranslation = true
        captionEl.textContent = translatedCaption
        setTranslateLabel("See original")
        return
      }
      setTranslateBusy(true)
      setTranslateLabel("Translating")
      window.clearTimeout(translateTimer)
      translateTimer = window.setTimeout(failTranslate, 125000)
      this.pushEvent("translate_caption", currentSlot, (reply) => {
        if (reply?.translated_caption) {
          applyTranslation(reply.translated_caption)
          return
        }
        if (reply?.pending) return
        failTranslate()
      })
    })

    this.handleEvent("caption_translated", (payload) => {
      if (!currentSlot) return
      if (payload.x !== currentSlot.x || payload.y !== currentSlot.y) return
      if (!payload.ok || !payload.translated_caption) {
        failTranslate(Boolean(payload.silent))
        return
      }
      applyTranslation(payload.translated_caption)
    })

    document.getElementById("slot-card-close")?.addEventListener("click", close)
    document.getElementById("slot-card-backdrop")?.addEventListener("click", close)
    this._onEsc = (event) => {
      if (event.key !== "Escape" || root.hidden) return
      close()
    }
    window.addEventListener("keydown", this._onEsc)
    this._closeSlotCard = close
    this._closeSlotCardSilent = silentClose
    this._openSlotCard = open
  },

  destroyed() {
    // The carry-over frame may still be up if the view leaves this early.
    if (this._seamEl && this._seamEl.parentNode) {
      this._seamEl.parentNode.removeChild(this._seamEl)
    }
    this._seamEl = null
    this.abortTour?.({resume: false})
    this._cancelDive?.()
    this.stopWarmingPoll?.()
    if (this._hintTimer) window.clearTimeout(this._hintTimer)
    if (this._onOrient) window.removeEventListener("orientationchange", this._onOrient)
    if (this._onEsc) window.removeEventListener("keydown", this._onEsc)
    if (this._onTourEsc) window.removeEventListener("keydown", this._onTourEsc)
    this._qa?.teardown()
    this._tapCleanup?.()
    this._closeSlotCard?.()
    this.map?.remove()
    this.map = null
  },
}

function num(value, fallback) {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n))
}

function posMod(n, m) {
  return ((n % m) + m) % m
}

function smoothstep(edge0, edge1, x) {
  const t = clamp((x - edge0) / (edge1 - edge0), 0, 1)
  return t * t * t * (t * (t * 6 - 15) + 10)
}


